import { IncidentType, ObservationType } from '../types/domain';

export const WARNING_COOLDOWN_MS = 12_000;
export const MAX_WARNINGS = 3;

export type BrowserViolationKind =
  | 'tab_switch'
  | 'window_blur'
  | 'visibility_hidden'
  | 'fullscreen_exit'
  | 'attempted_zoom'
  | 'keyboard_shortcut'
  | 'clipboard';

export interface CandidateWarning {
  id: string;
  warningNumber?: number;
  eventType: BrowserViolationKind | ObservationType;
  timestamp: string;
  message: string;
  tone: 'yellow' | 'orange' | 'red' | 'green';
  title?: string;
}

export interface WarningDecision {
  shouldWarn: boolean;
  warning?: CandidateWarning;
  warningCount: number;
}

const groupedEvents = new Set<BrowserViolationKind>(['tab_switch', 'window_blur', 'visibility_hidden']);

export function browserViolationToIncident(type: BrowserViolationKind): IncidentType {
  if (type === 'visibility_hidden') return 'tab_switch';
  if (type === 'clipboard') return 'clipboard_paste';
  return type;
}

export function canShowWarning(previousType: BrowserViolationKind | undefined, previousAt: number, nextType: BrowserViolationKind, now: number): boolean {
  if (now - previousAt < WARNING_COOLDOWN_MS) return false;
  return !(previousType && groupedEvents.has(previousType) && groupedEvents.has(nextType) && now - previousAt < WARNING_COOLDOWN_MS * 2);
}

export function createWarning(warningNumber: number, eventType: BrowserViolationKind, timestamp = new Date().toISOString()): CandidateWarning {
  const capped = Math.min(warningNumber, MAX_WARNINGS);
  const copy: Record<1 | 2 | 3, { message: string; tone: 'yellow' | 'orange' | 'red' }> = {
    1: {
      message: 'Return to the examination window and remain on the exam screen.',
      tone: 'yellow' as const
    },
    2: {
      message: 'Please remain on the examination screen while the session is active.',
      tone: 'orange' as const
    },
    3: {
      message: 'Return to the examination screen. This activity is recorded for session review.',
      tone: 'red' as const
    }
  };

  return {
    id: `${timestamp}-${eventType}-${capped}`,
    warningNumber: capped,
    eventType,
    timestamp,
    ...copy[capped as 1 | 2 | 3]
  };
}

export function nextWarning(
  warningCount: number,
  previousType: BrowserViolationKind | undefined,
  previousAt: number,
  nextType: BrowserViolationKind,
  now = Date.now()
): WarningDecision {
  if (!canShowWarning(previousType, previousAt, nextType, now)) {
    return { shouldWarn: false, warningCount };
  }
  const nextCount = Math.min(MAX_WARNINGS, warningCount + 1);
  return {
    shouldWarn: true,
    warningCount: nextCount,
    warning: createWarning(nextCount, nextType, new Date(now).toISOString())
  };
}
